import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, MessagesSquare, MessageCircle, LogIn, School as SchoolIcon, FileText, Calendar, X, Ban, UserCheck, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getSchoolUrl } from "@/lib/slug";

/**
 * Drawer that surfaces a full activity profile for one user:
 *  - Profile snapshot (name, email, role, joined, last active)
 *  - Quick actions: change role / ban-unban / delete (super admin only)
 *  - Totals for reviews / threads / replies / sessions
 *  - Recent 20 items from each collection, each linkable to the source page
 *
 * Used by Admin User Management. Super admin only endpoint.
 * Props:
 *  - userId, open, onOpenChange (drawer state)
 *  - currentUserId : id of the logged-in admin (guards self-modification)
 *  - onChanged     : parent callback to reload the user list after mutations
 */
export default function UserDetailDrawer({ userId, open, onOpenChange, currentUserId, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("reviews");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [banForm, setBanForm] = useState({ reason: "", duration_days: 0 });

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setData(null);
    api.get(`/admin/users/${userId}/detail`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const reload = () => {
    if (!userId) return;
    api.get(`/admin/users/${userId}/detail`).then((r) => setData(r.data)).catch(() => {});
    if (onChanged) onChanged();
  };

  const changeRole = async (role) => {
    if (!userId || busy) return;
    if (u && role === u.role) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success(`Peran diubah menjadi ${role}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah peran"); }
    finally { setBusy(false); }
  };

  const submitBan = async () => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}/ban`, {
        banned: true,
        reason: banForm.reason,
        duration_days: Number(banForm.duration_days) || 0,
      });
      toast.success(banForm.duration_days ? `Akun dinonaktifkan ${banForm.duration_days} hari` : "Akun dinonaktifkan permanen");
      setBanOpen(false);
      setBanForm({ reason: "", duration_days: 0 });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const unbanUser = async () => {
    if (!userId || busy) return;
    if (!window.confirm(`Aktifkan kembali akun ${u?.email || ""}?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}/ban`, { banned: false });
      toast.success("Akun diaktifkan kembali");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const deleteUser = async () => {
    if (!userId || busy) return;
    // Guard: the DELETE route expects the FULL user_id ("user_..."). Bail loudly
    // rather than firing a malformed request that would come back as 404.
    if (typeof userId !== "string" || !userId.startsWith("user_")) {
      toast.error("ID pengguna tidak valid");
      return;
    }
    if (!window.confirm(`HAPUS akun ${u?.email || ""} secara permanen?\n\nReview, thread, dan balasan tetap ada namun tidak lagi punya pemilik. Aksi ini tidak dapat dibatalkan.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Akun dihapus");
      if (onChanged) onChanged();
      onOpenChange(false);
    } catch (e) {
      // 404 = record already gone server-side (two tabs open, previous delete
      // succeeded but network response was lost, or another admin acted first).
      // Treat as success from the user's POV so we don't scare them with a red
      // toast, and refresh the list so the row disappears.
      if (e.response?.status === 404) {
        toast.success("Baris pengguna dibersihkan (data sudah tidak ada di server)");
        if (onChanged) onChanged();
        onOpenChange(false);
        return;
      }
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
    finally { setBusy(false); }
  };

  const u = data?.user;
  const c = data?.counts || {};
  const isSelf = !!(currentUserId && u && currentUserId === u.user_id);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="user-detail-drawer"
        className="w-full sm:max-w-xl overflow-y-auto bg-[#F9F8F6] p-0"
      >
        {loading || !data ? (
          <SkeletonBody />
        ) : data.error ? (
          <div className="p-6 text-sm text-[#B91C1C]" data-testid="user-detail-error">
            Gagal memuat detail pengguna.
          </div>
        ) : (
          <>
            {/* Header */}
            <SheetHeader className="px-6 pt-6 pb-4 bg-white border-b border-[#EAE7E0]">
              <SheetTitle asChild>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8A8F99] mb-1">Detail pengguna</div>
                  <div className="flex items-start gap-3">
                    <Avatar name={u.name} picture={u.picture} />
                    <div className="min-w-0">
                      <div className="font-serif-display text-2xl text-[#1F2937] leading-tight" data-testid="drawer-user-name">{u.name}</div>
                      <div className="text-sm text-[#4B5563] truncate" data-testid="drawer-user-email">{u.email}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#EAE7E0] text-[#1F2937]" data-testid="drawer-user-role">
                          {u.role}
                        </span>
                        {u.banned && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]">
                            Dinonaktifkan
                          </span>
                        )}
                        {u.level_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#FDF6EF] text-[#BE5730] border border-[#F3E1D0]">
                            {u.level_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SheetTitle>

              {/* Meta lines */}
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                <MetaLine icon={<Calendar size={12} />} label="Bergabung" value={fmtDate(u.created_at)} testid="drawer-meta-joined" />
                <MetaLine icon={<LogIn size={12} />} label="Login terakhir" value={fmtDateTime(u.last_login_at) || "—"} testid="drawer-meta-login" />
                {u.ban_reason && (
                  <MetaLine icon={<X size={12} />} label="Alasan ban" value={u.ban_reason} testid="drawer-meta-ban-reason" />
                )}
              </div>

              {/* Quick actions — hide entirely if editing your own account */}
              {!isSelf && (
                <div className="mt-4 rounded-2xl bg-[#F9F8F6] border border-[#EAE7E0] p-3" data-testid="drawer-quick-actions">
                  <div className="text-[10px] uppercase tracking-widest text-[#8A8F99] mb-2 flex items-center gap-1.5">
                    <Shield size={11} strokeWidth={2.25} /> Aksi Cepat
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Role picker */}
                    <div className="inline-flex items-center gap-1.5">
                      <span className="text-[11px] text-[#4B5563]">Peran</span>
                      <Select value={u.role} onValueChange={changeRole} disabled={busy}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="drawer-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="school_admin">School Admin</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="superadmin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Ban / Unban */}
                    {u.banned ? (
                      <Button
                        variant="outline"
                        onClick={unbanUser}
                        disabled={busy}
                        data-testid="drawer-unban-btn"
                        className="rounded-full border-[#166534] text-[#166534] hover:bg-[#F0FDF4] h-8 text-xs"
                      >
                        <UserCheck size={12} className="mr-1" /> Aktifkan
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => { setBanForm({ reason: "", duration_days: 0 }); setBanOpen(true); }}
                        disabled={busy}
                        data-testid="drawer-ban-btn"
                        className="rounded-full border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEF2F2] h-8 text-xs"
                      >
                        <Ban size={12} className="mr-1" /> Ban
                      </Button>
                    )}

                    {/* Delete */}
                    <Button
                      variant="outline"
                      onClick={deleteUser}
                      disabled={busy}
                      data-testid="drawer-delete-btn"
                      className="rounded-full border-[#EAE7E0] text-[#4B5563] hover:border-[#B91C1C] hover:text-[#B91C1C] hover:bg-[#FEF2F2] h-8 text-xs ml-auto"
                    >
                      <Trash2 size={12} className="mr-1" /> Hapus
                    </Button>
                  </div>
                </div>
              )}

              {isSelf && (
                <div className="mt-4 rounded-2xl bg-[#FEF3C7] border border-[#FDE68A] p-3 text-[11px] text-[#854D0E]" data-testid="drawer-self-notice">
                  Ini akun Anda sendiri — aksi ban / ubah peran / hapus dinonaktifkan untuk keamanan.
                </div>
              )}

              {/* Counts row */}
              <div className="mt-4 grid grid-cols-4 gap-2">
                <Stat label="Review" value={c.reviews || 0} testid="drawer-stat-reviews" />
                <Stat label="Thread" value={c.threads || 0} testid="drawer-stat-threads" />
                <Stat label="Balasan" value={c.replies || 0} testid="drawer-stat-replies" />
                <Stat label="Sesi login" value={c.sessions || 0} testid="drawer-stat-sessions" />
              </div>
            </SheetHeader>

            {/* Tabs */}
            <div className="sticky top-0 z-10 bg-[#F9F8F6] border-b border-[#EAE7E0] px-6 pt-3 pb-2">
              <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
                <TabButton active={tab === "reviews"} onClick={() => setTab("reviews")} testid="drawer-tab-reviews">
                  <Star size={13} strokeWidth={2.25} /> Review <Count n={c.reviews} active={tab === "reviews"} />
                </TabButton>
                <TabButton active={tab === "threads"} onClick={() => setTab("threads")} testid="drawer-tab-threads">
                  <MessagesSquare size={13} strokeWidth={2.25} /> Thread <Count n={c.threads} active={tab === "threads"} />
                </TabButton>
                <TabButton active={tab === "replies"} onClick={() => setTab("replies")} testid="drawer-tab-replies">
                  <MessageCircle size={13} strokeWidth={2.25} /> Balasan <Count n={c.replies} active={tab === "replies"} />
                </TabButton>
                <TabButton active={tab === "sessions"} onClick={() => setTab("sessions")} testid="drawer-tab-sessions">
                  <LogIn size={13} strokeWidth={2.25} /> Riwayat login <Count n={c.sessions} active={tab === "sessions"} />
                </TabButton>
              </div>
            </div>

            {/* Panel content */}
            <div className="px-6 py-5 space-y-3">
              {tab === "reviews" && <ReviewsPanel items={data.reviews} />}
              {tab === "threads" && <ThreadsPanel items={data.threads} />}
              {tab === "replies" && <RepliesPanel items={data.replies} />}
              {tab === "sessions" && <SessionsPanel items={data.sessions} />}
            </div>

            {/* Ban dialog (nested inside the sheet) */}
            <Dialog open={banOpen} onOpenChange={setBanOpen}>
              <DialogContent data-testid="drawer-ban-dialog">
                <DialogHeader>
                  <DialogTitle>Nonaktifkan akun {u.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Alasan</div>
                    <Input
                      placeholder="mis: pelanggaran pedoman komunitas — komentar toksik"
                      value={banForm.reason}
                      onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                      data-testid="drawer-ban-reason"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Durasi</div>
                    <Select
                      value={String(banForm.duration_days || 0)}
                      onValueChange={(v) => setBanForm({ ...banForm, duration_days: Number(v) })}
                    >
                      <SelectTrigger data-testid="drawer-ban-duration"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Permanen</SelectItem>
                        <SelectItem value="7">7 hari</SelectItem>
                        <SelectItem value="30">30 hari</SelectItem>
                        <SelectItem value="90">90 hari</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={() => setBanOpen(false)} className="rounded-full h-9">Batal</Button>
                    <Button
                      onClick={submitBan}
                      disabled={busy}
                      data-testid="drawer-ban-submit"
                      className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white h-9"
                    >
                      Nonaktifkan
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------- Sub-components ----------------

function Avatar({ name, picture }) {
  if (picture) return <img src={picture} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />;
  const initials = (name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-14 h-14 rounded-full bg-[#1E3A8A] text-white grid place-items-center font-serif-display text-xl shrink-0">
      {initials || "?"}
    </div>
  );
}

function MetaLine({ icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 text-[#4B5563]" data-testid={testid}>
      <span className="text-[#8A8F99]">{icon}</span>
      <span className="text-[10px] uppercase tracking-widest text-[#8A8F99]">{label}</span>
      <span className="text-[#1F2937] font-medium truncate">{value}</span>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-[#F9F8F6] border border-[#EAE7E0] px-3 py-2 text-center" data-testid={testid}>
      <div className="text-lg font-serif-display text-[#1F2937] tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8A8F99]">{label}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors " +
        (active ? "bg-[#1E3A8A] text-white" : "bg-white text-[#4B5563] border border-[#EAE7E0] hover:text-[#1F2937]")
      }
    >
      {children}
    </button>
  );
}

function Count({ n, active }) {
  if (n === undefined || n === null) return null;
  return (
    <span className={"ml-0.5 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full " + (active ? "bg-white/20" : "bg-[#F3F1ED] text-[#4B5563]")}>{n}</span>
  );
}

function EmptyPanel({ children }) {
  return <div className="text-sm text-[#8A8F99] italic py-6 text-center" data-testid="drawer-panel-empty">{children}</div>;
}

function ReviewsPanel({ items }) {
  if (!items?.length) return <EmptyPanel>Belum ada review.</EmptyPanel>;
  return items.map((r, i) => (
    <div key={r.review_id} data-testid={`drawer-review-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        {r.school_name ? (
          <Link
            to={getSchoolUrl({ school_id: r.school_id, name: r.school_name, city: r.school_city }, { hash: `review-${r.review_id}` })}
            className="inline-flex items-center gap-1 text-[#1E3A8A] hover:text-[#D2673E] font-medium"
            data-testid={`drawer-review-${i}-school`}
          >
            <SchoolIcon size={12} /> {r.school_name}
          </Link>
        ) : (
          <span className="text-[#8A8F99] italic">(sekolah dihapus)</span>
        )}
        {r.school_city && <span className="text-[#8A8F99]">· {r.school_city}</span>}
        <StatusBadge status={r.status} isVerified={r.is_verified} />
        {r.edit_count > 0 && (
          <span className="text-[10px] italic text-[#8A8F99]">Diedit {r.edit_count}×</span>
        )}
      </div>
      {r.title && <div className="mt-2 text-sm text-[#1F2937] font-semibold leading-snug">{r.title}</div>}
      {r.body_excerpt && <p className="mt-1 text-xs text-[#4B5563] leading-relaxed line-clamp-3">{r.body_excerpt}</p>}
      <div className="mt-2 text-[10px] text-[#8A8F99]">{fmtDateTime(r.created_at)}</div>
    </div>
  ));
}

function ThreadsPanel({ items }) {
  if (!items?.length) return <EmptyPanel>Belum pernah membuat thread.</EmptyPanel>;
  return items.map((t, i) => (
    <div key={t.thread_id} data-testid={`drawer-thread-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
      <Link
        to={`/forum/${t.thread_id}`}
        className="block group"
        data-testid={`drawer-thread-${i}-link`}
      >
        <div className="text-[11px] text-[#8A8F99] uppercase tracking-widest">{t.category}</div>
        <div className="mt-1 text-sm text-[#1F2937] font-semibold group-hover:text-[#D2673E] leading-snug">
          {t.title}
        </div>
      </Link>
      <div className="mt-2 text-[10px] text-[#8A8F99] flex items-center gap-2 flex-wrap">
        <span>{t.reply_count || 0} balasan</span>
        <span>· {t.upvotes || 0} suka</span>
        {t.school_name && <span>· {t.school_name}</span>}
        <span>· {fmtDateTime(t.created_at)}</span>
      </div>
    </div>
  ));
}

function RepliesPanel({ items }) {
  if (!items?.length) return <EmptyPanel>Belum pernah menulis balasan.</EmptyPanel>;
  return items.map((r, i) => (
    <div key={r.reply_id} data-testid={`drawer-reply-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
      <Link
        to={`/forum/${r.thread_id}#reply-${r.reply_id}`}
        className="text-[12px] text-[#1E3A8A] hover:text-[#D2673E] font-medium inline-flex items-center gap-1"
        data-testid={`drawer-reply-${i}-link`}
      >
        <FileText size={12} /> {r.thread_title}
      </Link>
      <p className="mt-1.5 text-xs text-[#1F2937] leading-relaxed line-clamp-3 whitespace-pre-wrap">{r.body_excerpt}</p>
      <div className="mt-2 text-[10px] text-[#8A8F99]">{r.likes || 0} suka · {fmtDateTime(r.created_at)}</div>
    </div>
  ));
}

function SessionsPanel({ items }) {
  if (!items?.length) return <EmptyPanel>Belum ada catatan sesi login.</EmptyPanel>;
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-[#F3F1ED] text-[#4B5563]">
          <tr>
            <th className="text-left p-3 uppercase tracking-widest text-[10px]">Waktu login</th>
            <th className="text-left p-3 uppercase tracking-widest text-[10px]">Kedaluwarsa</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s, i) => (
            <tr key={i} data-testid={`drawer-session-${i}`} className="border-t border-[#EAE7E0]">
              <td className="p-3 text-[#1F2937] tabular-nums">{fmtDateTime(s.created_at)}</td>
              <td className="p-3 text-[#4B5563] tabular-nums">{fmtDateTime(s.expires_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status, isVerified }) {
  const map = {
    published: { bg: "#DCFCE7", fg: "#166534", label: "Ditayang" },
    pending: { bg: "#FEF3C7", fg: "#854D0E", label: "Menunggu" },
    rejected: { bg: "#FEE2E2", fg: "#B91C1C", label: "Ditolak" },
  };
  const m = map[status] || { bg: "#EAE7E0", fg: "#4B5563", label: status || "—" };
  return (
    <>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ backgroundColor: m.bg, color: m.fg }}>
        {m.label}
      </span>
      {isVerified && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#D1FAE5] text-[#047857] border border-[#A7F3D0]">
          Diverifikasi
        </span>
      )}
    </>
  );
}

function SkeletonBody() {
  return (
    <div className="p-6 space-y-4" data-testid="drawer-loading">
      <div className="h-14 w-14 rounded-full bg-[#EAE7E0] animate-pulse" />
      <div className="h-6 w-40 bg-[#EAE7E0] rounded animate-pulse" />
      <div className="h-4 w-52 bg-[#F3F1ED] rounded animate-pulse" />
      <div className="grid grid-cols-4 gap-2 mt-2">
        {[0, 1, 2, 3].map((k) => <div key={k} className="h-14 rounded-xl bg-[#F3F1ED] animate-pulse" />)}
      </div>
      <div className="h-24 rounded-2xl bg-[#F3F1ED] animate-pulse mt-4" />
      <div className="h-24 rounded-2xl bg-[#F3F1ED] animate-pulse" />
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}
